import React, { ReactNode } from "react";
import { render } from "react-dom";

import { GridItemsHeight, GridLayout, GridLayoutProps } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { createRootComponent, StoreManager } from "@multiweb/reactSetup";
import { safeParseInt } from "@multiweb/reactUtils";
import { CaseInsensitiveMap } from "@multiweb/types/multiweb";
import { IDisposable } from "Global/Base/IDisposable";
import { AppContext } from "../../../App";
import { Control } from "../../UI/UI/Controls/Control";
import { FormControlParams } from "../../UI/UI/Controls/ControlInterfaces";
import { IGeneralReactFrameConnector, IPostInitialize } from "../types";
import {
    DataGridItemRenderer,
    DetailFrameItemRenderer,
    GeneralItemRenderer,
    GridLayoutItemRenderer,
    GroupBoxItemRenderer,
    IItemRenderer,
    ItemRendererConstructor,
    TreeFrameItemRenderer,
} from "./GridLayoutItemRenderers";
import { applyRendererMixins } from "./GridLayoutItemRenderers/mixins";

const { stringToEnumValue } = bridge;

/**
 * Slouží k propojení stávající aplikace a GridLayoutu.
 */
export class GridLayoutConnector extends Control implements IGeneralReactFrameConnector, IDisposable, IPostInitialize {
    private _itemRenderers: IItemRenderer[] = [];
    private readonly _gridItemsHeight: GridItemsHeight;
    private static _registeredRenderers: CaseInsensitiveMap<ItemRendererConstructor> =
        new CaseInsensitiveMap<ItemRendererConstructor>(); // Seznam registrovaných rendererů

    public developmentMode: boolean;

    /**
     * Zaregistruje specifický renderer pro daný `tscControl`.
     *
     * @param controlString Hodnota z atributu `data-tsccontrol`.
     * @param renderer
     */
    public static registerRenderer(controlString: string, renderer: ItemRendererConstructor): void {
        if (!GridLayoutConnector._registeredRenderers.has(controlString))
            GridLayoutConnector._registeredRenderers.set(controlString, renderer);
    }

    /**
     * Vrací renderer pro zadaný `tscControl`. Prochází zaregistrované renderery, pokud není žádný zaregistrovaný, vrací defaultní.
     *
     * @param controlString Hodnota z atributu `data-tsccontrol`.
     * @returns Renderer pro zadaný `tscControl`.
     */
    private static _getRendererByControlString(controlString: string): ItemRendererConstructor {
        if (GridLayoutConnector._registeredRenderers.has(controlString)) {
            return GridLayoutConnector._registeredRenderers.get(controlString);
        }
        return GeneralItemRenderer;
    }

    constructor(params: FormControlParams) {
        super(params);
        const { element: rootElement } = params;

        this._gridItemsHeight = stringToEnumValue(
            GridItemsHeight,
            this.element.getAttribute("data-griditemsheight") || GridItemsHeight.takeHighest,
        );
        /**
         * Store inicializuji, protože mixiny a renderery na jej mohou měnit (visibility, lokalizace, ...),
         * ale není potřeba vytvářet RootComponentu jako v LLF,
         * protože komponenty `GridLayoutComponent` a `GridLayoutItem` Store nevyužívají.
         */
        this.developmentMode = !!AppContext.instance.configuration.developmentMode;
        StoreManager.initializeStore(this.developmentMode);

        /**
         * Získání rendererů podle vnitřních komponent. Například GroupBox potřebuje
         * speciální implementaci rendereru.
         */
        const { commonElements, rightsElements } = this._filterChildrenByType([...rootElement.children]);
        this._itemRenderers = commonElements.map((element: HTMLElement) => {
            const Renderer = applyRendererMixins(
                GridLayoutConnector._getRendererByControlString(
                    element.firstElementChild.getAttribute("data-tsccontrol"),
                ),
            );
            return new Renderer({ ...params, element }, this._gridItemsHeight);
        });

        this.renderComponent();

        //Dočasný fix, jak řešit práva na react komponentách. Do budoucna (po merge větve s novým systémem vyhodnocování práv), bude potřeba vymyslet lepší řešení, třeba nějakou obálku v reactu, která se bude starat o práva.
        rightsElements.forEach((e) => rootElement.appendChild(e));
    }

    public postInitialize(): void {
        this._itemRenderers.forEach((itemRenderer) => itemRenderer.initialize());
    }

    /**
     * Uklidí komponentu před jejím odstraněním z DOMu.
     */
    public override dispose(): void {
        this._itemRenderers.forEach((renderer) => renderer.dispose());
    }

    /**
     * Vyrenderuje GridLayout i s vniřními itemy.
     */
    public renderComponent(): void {
        if (!this.element) {
            return;
        }
        const RootComponent = createRootComponent(this.developmentMode);
        const gridLayoutProps: GridLayoutProps = {
            columns: safeParseInt(this.element.dataset.columns),
            columnsS: safeParseInt(this.element.dataset.columnss),
            columnsM: safeParseInt(this.element.dataset.columnsm),
            columnsL: safeParseInt(this.element.dataset.columnsl),
            columnsXl: safeParseInt(this.element.dataset.columnsxl),
            columnsXxl: safeParseInt(this.element.dataset.columnsxxl),
            gap: safeParseInt(this.element.dataset.gridgap),
            gapS: safeParseInt(this.element.dataset.gridgaps),
            gapM: safeParseInt(this.element.dataset.gridgapm),
            gapL: safeParseInt(this.element.dataset.gridgapl),
            gapXl: safeParseInt(this.element.dataset.gridgapxl),
            gapXxl: safeParseInt(this.element.dataset.gridgapxxl),
        };

        render(
            <RootComponent>
                <GridLayout {...gridLayoutProps}>{this._renderGridLayoutItems()}</GridLayout>
            </RootComponent>,
            this.element,
        );
    }

    /** Funkce projde HTML elementy a rozdělí elementy na běžné a elementy, které jsou určeny pro práva */
    private _filterChildrenByType(elements: Element[]): { commonElements: Element[]; rightsElements: Element[] } {
        const commonElements: Element[] = [];
        const rightsElements: Element[] = [];
        elements.forEach((e) => {
            if (e.getAttribute("data-tscitem")?.startsWith("Rights.")) {
                rightsElements.push(e);
            } else {
                commonElements.push(e);
            }
        });
        return { commonElements, rightsElements };
    }

    /**
     * Funkce vrací pole GridLayoutMultiwebItemů vyrenderovaných jednotlivými renderery.
     */
    private _renderGridLayoutItems(): ReactNode[] {
        return this._itemRenderers.map((render) => render.render());
    }
}

GridLayoutConnector.registerRenderer("GroupBox", GroupBoxItemRenderer);
GridLayoutConnector.registerRenderer("GridLayout", GridLayoutItemRenderer);
GridLayoutConnector.registerRenderer("ListFrame", DataGridItemRenderer);
GridLayoutConnector.registerRenderer("DetailFrame", DetailFrameItemRenderer);
GridLayoutConnector.registerRenderer("TreeListFrame", TreeFrameItemRenderer);
